/**
 * Product-page Star Rating Badge clicks scroll to the Cards Carousel.
 * Judge.me only jumps to a Review Widget (#judgeme_product_reviews).
 * This PDP uses Cards Carousel instead, so native badge clicks do nothing.
 *
 * Cards Carousel headers always render shop-wide verified stats. When a
 * carousel is set to current-product reviews, replace those with the
 * product badge average and count.
 */
import { prefersReducedMotion } from '@theme/utilities';

const BADGE_SELECTOR = '.jdgm-preview-badge, .jdgm-prev-badge';
const DETAILS_SELECTOR = '.product-details';
const CARD_SELECTOR = '.product-card, .related-product-card';
const CAROUSEL_SELECTOR = '.jdgm-cards-carousel';
const CAROUSEL_ID = 'product-reviews';
const CURRENT_PRODUCT_RE = /reviews_selection\s*:\s*['"]current_product['"]/;

/** @type {WeakSet<Element>} */
const watchedCarousels = new WeakSet();

/**
 * @param {EventTarget | null} target
 * @returns {Element | null}
 */
function productDetailsBadge(target) {
  const el =
    target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
  if (!el) return null;

  const badge = el.closest(BADGE_SELECTOR);
  if (!badge) return null;
  if (badge.closest(CARD_SELECTOR)) return null;
  if (!badge.closest(DETAILS_SELECTOR)) return null;

  return badge;
}

/** @returns {HTMLElement | null} */
function carouselTarget() {
  const carousel = document.querySelector(CAROUSEL_SELECTOR);
  if (!(carousel instanceof HTMLElement)) return null;
  if (!carousel.id) carousel.id = CAROUSEL_ID;

  const wrapper = carousel.closest('.shopify-block, .shopify-section');
  return wrapper instanceof HTMLElement ? wrapper : carousel;
}

function scrollToCarousel() {
  const carousel = carouselTarget();
  if (!carousel) return false;

  carousel.scrollIntoView({
    behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    block: 'start',
  });
  return true;
}

function markBadges() {
  document.querySelectorAll(`${DETAILS_SELECTOR} ${BADGE_SELECTOR}`).forEach((badge) => {
    if (badge.closest(CARD_SELECTOR)) return;
    badge.classList.add('jdgm-preview-badge--with-link');
  });
}

/**
 * @returns {{ rating: number, count: number } | null}
 */
function productBadgeStats() {
  const badge = document.querySelector(`${DETAILS_SELECTOR} .jdgm-prev-badge[data-average-rating]`);
  if (!(badge instanceof HTMLElement)) return null;

  const rating = Number.parseFloat(badge.getAttribute('data-average-rating') || '');
  const count = Number.parseInt(badge.getAttribute('data-number-of-reviews') || '', 10);
  if (!Number.isFinite(rating) || !Number.isFinite(count) || count < 1) return null;

  return { rating, count };
}

/**
 * @param {number} rating
 * @returns {string}
 */
function formatRating(rating) {
  return (Math.round(rating * 10) / 10).toFixed(1);
}

/**
 * @param {Element} carousel
 * @returns {boolean}
 */
function isCurrentProductCarousel(carousel) {
  if (carousel.getAttribute('data-k8-reviews-selection') === 'current_product') return true;

  const root = carousel.closest('.shopify-block, .shopify-app-block') || carousel.parentElement;
  if (!root) return false;

  for (const script of root.querySelectorAll('script')) {
    if (CURRENT_PRODUCT_RE.test(script.textContent || '')) {
      carousel.setAttribute('data-k8-reviews-selection', 'current_product');
      return true;
    }
  }

  return false;
}

/**
 * @param {Element} ratingText
 * @param {string} ratingLabel
 */
function setRatingNumber(ratingText, ratingLabel) {
  for (const node of ratingText.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && /\d/.test(node.textContent || '')) {
      const suffix = /^\s/.test(node.textContent || '') ? '' : ' ';
      const next = `${ratingLabel}${suffix}`;
      if (node.textContent !== next) node.textContent = next;
      return;
    }
  }
}

/**
 * @param {Element} carousel
 */
function syncCarouselHeader(carousel) {
  if (!isCurrentProductCarousel(carousel)) return;

  const stats = productBadgeStats();
  if (!stats) return;

  const ratingLabel = formatRating(stats.rating);
  const countLabel = String(stats.count);

  const countEl = carousel.querySelector('.jdgm-reviews-count');
  if (countEl && countEl.textContent !== countLabel) countEl.textContent = countLabel;

  const ratingText = carousel.querySelector('.jdgm-rating-text');
  if (ratingText) setRatingNumber(ratingText, ratingLabel);

  const stars = carousel.querySelector('.jdgm-stars');
  if (stars) {
    const label = `${ratingLabel} out of 5 stars`;
    if (stars.getAttribute('aria-label') !== label) stars.setAttribute('aria-label', label);
  }
}

function syncCarouselHeaders() {
  document.querySelectorAll(CAROUSEL_SELECTOR).forEach((carousel) => {
    if (!(carousel instanceof HTMLElement)) return;
    syncCarouselHeader(carousel);
    watchCarousel(carousel);
  });
}

/**
 * @param {HTMLElement} carousel
 */
function watchCarousel(carousel) {
  if (watchedCarousels.has(carousel)) return;
  if (!isCurrentProductCarousel(carousel)) return;

  watchedCarousels.add(carousel);
  const observer = new MutationObserver(() => syncCarouselHeader(carousel));
  observer.observe(carousel, { childList: true, subtree: true, characterData: true });
}

/**
 * @param {MouseEvent} event
 */
function onClick(event) {
  if (!productDetailsBadge(event.target)) return;
  if (!carouselTarget()) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  scrollToCarousel();
}

/**
 * @param {KeyboardEvent} event
 */
function onKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  if (!productDetailsBadge(event.target)) return;
  if (!carouselTarget()) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  scrollToCarousel();
}

document.addEventListener('click', onClick, true);
document.addEventListener('keydown', onKeydown, true);

function enhance() {
  markBadges();
  syncCarouselHeaders();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', enhance);
} else {
  enhance();
}

window.addEventListener('load', enhance);
document.addEventListener('jdgm.doneLoadingCss', enhance);
